/**
 * Central registry for the patient-detail tab system.
 *
 * Adding a new patient tab is a single entry in `PATIENT_TABS` below —
 * the tab bar, the active-tab logic and the per-tab error boundary are
 * applied automatically by `<PatientDetail />`. Mirrors the route
 * registry pattern in `route-registry.ts`.
 *
 * Since PR 1.3 ("Lean & Clean") the tab bar is flat: container tabs
 * replace the old 4-group / 20-tab structure. Tabs that merge several
 * related screens (Beratung, Messwerte, Analyse, Portal, …) render a
 * thin container component that wraps a shadcn `<Tabs>` around the
 * existing underlying tab components — the leaf components themselves
 * are unchanged. Everything that *was* available is still available;
 * it is just grouped for a calmer UI. The UX-Audit Phase 1.2 follow-up
 * merged the Portal-Zugriff/Portal-Aktivität pair into a single
 * `portal` container, taking the top-level count from 12 down to 11.
 *
 * Visibility:
 *   - `visibility: 'hidden'` removes a tab from the tab bar but keeps
 *     it reachable via deep link (`?tab=<id>`) and the command palette
 *     (PR 1.4). Used for `outcome`, which is still supported but not
 *     part of the daily workflow.
 *   - The other tabs default to `primary` and all render flat.
 *
 * Kuratierte Akte ("Make it simple"):
 *   `pinned: true` markiert die Kern-Tabs, die immer direkt in der
 *   Tab-Bar sichtbar bleiben. Alle übrigen sichtbaren Tabs wandern
 *   hinter den "Mehr"-Launcher (Tile-Grid-Dialog, analog zum
 *   FunctionLauncher der Chat-First-Shell). Die Aufteilung übernimmt
 *   `partitionPatientTabs()` unten — pure function, damit testbar.
 *   Der gerade aktive Tab erscheint immer in der Bar, auch wenn er
 *   nicht gepinnt ist (Prinzip 1: Status sichtbar).
 *
 * Why each tab still uses an explicit `render(ctx)` adapter:
 *   The underlying components have heterogeneous prop signatures —
 *   some only need `{patientId, orgId}`, others want `{patient}`,
 *   `GncpTab` wants the entire context. The adapter keeps every
 *   change *additive*: we never touch a leaf component when moving
 *   it from a standalone tab into a container.
 */

import { lazy, type ReactNode } from 'react';
import {
  Activity,
  BarChart2,
  ClipboardCheck,
  ClipboardList,
  FileText,
  FolderOpen,
  History,
  Hospital,
  LayoutDashboard,
  MessageSquare,
  Pill,
  Smartphone,
  Sparkles,
  TrendingUp,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';
import type { Feature, SettingType } from '@/hooks/useFeatures.constants';
import type { FeatureFlag } from '@/config/feature-flags';
import type { Scope, Visibility } from './types';
import type { NavOverrides } from './visibility';

/**
 * Context passed to every patient tab adapter. Tabs only get what they
 * need; keeping this small means a new tab can't accidentally couple
 * itself to the entire patient page.
 */
export interface PatientTabContext {
  patientId: string;
  orgId: string;
  userId: string;
  patient: Record<string, unknown> | null;
  therapistName: string;
  orgName: string;
  orgLogoUrl: string | null;
  /** Optional GNCP prefill (used when arriving from a session recording). */
  gncpPrefill?: unknown;
  /**
   * PR 3.1: the source `session_recordings.id` that produced the
   * prefill above. After the clinician saves the pre-filled GNCP,
   * `GncpTab` uses this to set `session_recordings.gncp_entry_id` on
   * the recording — the Timeline view (Phase 3.2) chains off that
   * column.
   */
  gncpPrefillSourceRecordingId?: string | null;
  onGncpPrefillConsumed?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onGncpDraftReady?: (...args: any[]) => void;
}

/**
 * Runtime information the tab-enablement predicate receives. Split out
 * into an options object so the predicate can be extended later without
 * breaking existing entries.
 */
export interface PatientTabEnablementContext {
  hasFeature: (key: Feature) => boolean;
  isInpatient: boolean;
  settingType: SettingType;
}

/** A single addressable tab in the patient detail page. */
export interface PatientTabModule {
  /** Stable id used as React key + URL fragment. */
  id: string;
  /** Sidebar label shown in the tab list. */
  label: string;
  /**
   * Icon für die Kachel im "Mehr"-Launcher (kuratierte Akte). Die Bar
   * selbst rendert weiterhin nur Text-Buttons; das Icon braucht nur
   * das Tile-Grid. Optional, damit Bestandseinträge ohne Icon nicht
   * brechen — der Launcher fällt dann auf ein generisches Icon zurück.
   */
  icon?: LucideIcon;
  /**
   * Kuratierte Akte: `true` = Kern-Tab, bleibt immer direkt in der
   * Tab-Bar sichtbar. Alle anderen sichtbaren Tabs landen hinter dem
   * "Mehr"-Launcher. Unabhängig vom Gating — ein gepinnter Tab, dessen
   * Feature fehlt, taucht trotzdem nirgends auf (`resolvePatientTabs`
   * filtert vor der Partition).
   */
  pinned?: boolean;
  /**
   * Lower numbers appear earlier (gaps of 10 leave room for future
   * inserts without renumbering).
   */
  order?: number;
  /**
   * Feature flag the org must have enabled for the tab to appear.
   * For simple gates (a single flag controls the whole tab). For
   * OR-of-features gating (e.g. "Analyse" = bls OR recipe), use
   * `isEnabled` instead — the two are mutually exclusive.
   */
  feature?: Feature;
  /**
   * Static MVP kill switch (see `src/config/feature-flags.ts`). Orthogonal
   * to `feature` (per-org purchase) and `isEnabled` (per-patient/setting
   * predicate): when set and the flag is `false`, the tab is removed from
   * the tab bar globally, regardless of org features. Enforced by the
   * `PatientDetail` renderer (deep links to a disabled tab fall back to
   * the default tab) so the registry function stays pure and testable.
   */
  featureFlag?: FeatureFlag;
  /**
   * Custom visibility predicate for container tabs that cannot be
   * expressed with a single `feature`. Takes the full enablement
   * context (feature checker, inpatient status, setting type) and
   * returns `true` iff the container should appear. When set, wins
   * over `feature`.
   */
  isEnabled?: (ctx: PatientTabEnablementContext) => boolean;
  /**
   * Inpatient-only tab. When `true`, the tab only renders for patients
   * whose `admission_status !== 'ambulant'` — unless the org is a
   * klinik, in which case every patient is treated as inpatient.
   */
  inpatientOnly?: boolean;
  /**
   * Org-level visibility: restrict the tab to `'praxis'` or `'klinik'`
   * setting-types, or allow it in `'both'` (default). Orthogonal to
   * `inpatientOnly`, which gates per *patient* admission status.
   * Superadmins bypass the filter so they can reach every tab for
   * support.
   *
   * Most tabs should leave this unset. Typical use: a tab that exists
   * exclusively for one product mode (e.g. a Klinik-only consult
   * inbox, or a Praxis-only GOÄ-honorar view).
   */
  scope?: Scope;
  /**
   * How prominently this tab is surfaced in the tab bar. Mirrors the
   * route-registry `Visibility` layer:
   *   primary   = shown flat in the main tab bar (default)
   *   secondary = reserved for a future "Mehr…" patient-tab accordion
   *   hidden    = not rendered in the tab bar but still reachable via
   *               deep link (`?tab=<id>`) and the command palette.
   *
   * Used in PR 1.3 to demote `outcome` without deleting it.
   */
  visibility?: Visibility;
  /**
   * Render the tab. The adapter receives the full context and is
   * responsible for forwarding only the props its component needs.
   */
  render: (ctx: PatientTabContext) => ReactNode;
}

// ─── Lazy-loaded container & leaf tab components ─────────────────────────
//
// `lazy()` per tab keeps PatientDetail's initial bundle small. The
// container components each internally lazy-load their sub-tab
// components, so switching between containers only pulls the chunks
// the user actually opens.

const OverviewTab               = lazy(() => import('@/components/patient/OverviewTab').then((m) => ({ default: m.OverviewTab })));
const DocumentsTab              = lazy(() => import('@/components/patient/DocumentsTab').then((m) => ({ default: m.DocumentsTab })));
const OutcomeTrackingTab        = lazy(() => import('@/components/patient/OutcomeTrackingTab').then((m) => ({ default: m.OutcomeTrackingTab })));

const BeratungTab               = lazy(() => import('@/components/patient/BeratungTab').then((m) => ({ default: m.BeratungTab })));
const MesswerteTab              = lazy(() => import('@/components/patient/MesswerteTab').then((m) => ({ default: m.MesswerteTab })));
const ErnaehrungsplanTab        = lazy(() => import('@/components/patient/ErnaehrungsplanTab').then((m) => ({ default: m.ErnaehrungsplanTab })));
const AnalyseTab                = lazy(() => import('@/components/patient/AnalyseTab').then((m) => ({ default: m.AnalyseTab })));
const AnamneseFragebogenTab     = lazy(() => import('@/components/patient/AnamneseFragebogenTab').then((m) => ({ default: m.AnamneseFragebogenTab })));
const VerordnungenAufgabenTab   = lazy(() => import('@/components/patient/VerordnungenAufgabenTab').then((m) => ({ default: m.VerordnungenAufgabenTab })));
const KlinikTab                 = lazy(() => import('@/components/patient/KlinikTab').then((m) => ({ default: m.KlinikTab })));
const PortalTab                 = lazy(() => import('@/components/patient/PortalTab').then((m) => ({ default: m.PortalTab })));
const MedikamenteTab            = lazy(() => import('@/components/patient/MedikamenteTab').then((m) => ({ default: m.MedikamenteTab })));
const PatientKiCompanionPanel   = lazy(() => import('@/components/ai/PatientKiCompanionPanel').then((m) => ({ default: m.PatientKiCompanionPanel })));
const PatientMessagesTab        = lazy(() => import('@/components/patient/PatientMessagesTab').then((m) => ({ default: m.PatientMessagesTab })));
const ActivityFeedTab           = lazy(() => import('@/components/patient/ActivityFeed').then((m) => ({ default: m.ActivityFeed })));

/**
 * The full patient-tab table. Order in this list controls *only* the
 * declaration order; tab-bar order is governed by `order`.
 */
export const PATIENT_TABS: readonly PatientTabModule[] = [
  // Kuratierte Akte — die Kern-Tabs (`pinned: true`) sind die "Basics"
  // aus dem UX-Plan: Übersicht, GNCP, Messwerte, Termine. GNCP und
  // Termine leben seit PR 1.3 beide im `beratung`-Container, daher
  // kollabieren die Basics auf wenige Registry-Ids. Nachrichten ist
  // im Praxis-MVP ebenfalls ein Hauptbereich, sofern Messaging aktiv ist.
  // Dazu kommt der
  // `klinik`-Container, der ohnehin nur im klinischen Kontext sichtbar
  // ist (Klinik-Org oder stationärer Patient) und dort gepinnt bleibt.
  {
    id: 'overview',
    label: 'Übersicht',
    icon: LayoutDashboard,
    order: 10,
    pinned: true,
    render: (ctx) => <OverviewTab patient={ctx.patient ?? {}} />,
  },
  {
    id: 'beratung',
    label: 'Dokumentation',
    icon: FileText,
    order: 20,
    featureFlag: 'gncp_documentation',
    // Enthält GNCP UND Termine (PR 1.3 Container) → deckt zwei der
    // vier Basics ab.
    pinned: true,
    render: (ctx) => <BeratungTab ctx={ctx} />,
  },
  {
    id: 'messwerte',
    label: 'Messwerte',
    icon: Activity,
    order: 30,
    pinned: true,
    // No `featureFlag`: Anthropometrie (Gewicht/BMI) is unconditional and
    // must always be reachable. The Laborwerte sub-section self-gates inside
    // the container via its own feature, so the static `lab_values` kill
    // switch (which legitimately gates the Labor add-on) must not hide the
    // whole tab — that would break the "sold ⇒ reachable" invariant for the
    // unconditional Anthropometrie part.
    render: (ctx) => <MesswerteTab ctx={ctx} />,
  },
  {
    id: 'plan',
    label: 'Ernährungsplan',
    icon: UtensilsCrossed,
    order: 40,
    featureFlag: 'diet_orders',
    feature: 'ernaehrungsplan_patient',
    render: (ctx) => <ErnaehrungsplanTab ctx={ctx} />,
  },
  {
    id: 'analyse',
    label: 'Analyse',
    icon: BarChart2,
    order: 50,
    featureFlag: 'lab_values',
    // Shown iff *either* BLS-Analyse or Rezeptanalyse is available.
    isEnabled: ({ hasFeature }) =>
      hasFeature('bls_ernaehrungsanalyse') || hasFeature('rezeptanalyse'),
    render: (ctx) => <AnalyseTab ctx={ctx} />,
  },
  {
    id: 'anamnese-fragebogen',
    label: 'Anamnese & Fragebögen',
    icon: ClipboardList,
    order: 60,
    // No `featureFlag`: this tab already self-gates via `isEnabled` on two
    // sold basis features (`fragebögen` / `diaetanamnese`). The
    // `patient_portal` static flag gates the separate PWA portal add-on and
    // must not kill this sold surface ("sold ⇒ reachable").
    // Shown iff *either* the generic Fragebögen module or the structured
    // Diätanamnese feature is enabled.
    isEnabled: ({ hasFeature }) =>
      hasFeature('fragebögen') || hasFeature('diaetanamnese'),
    render: (ctx) => <AnamneseFragebogenTab ctx={ctx} />,
  },
  {
    id: 'verordnungen-aufgaben',
    label: 'Indikationen',
    icon: ClipboardCheck,
    order: 70,
    featureFlag: 'diet_orders',
    // HMV is unconditional; Aufgaben is feature-gated inside.
    render: (ctx) => <VerordnungenAufgabenTab ctx={ctx} />,
  },
  {
    id: 'medikamente',
    label: 'Medikamente',
    icon: Pill,
    order: 75,
    featureFlag: 'lab_values',
    feature: 'medikamente',
    render: (ctx) => (
      <MedikamenteTab patientId={ctx.patientId} orgId={ctx.orgId} userId={ctx.userId} />
    ),
  },
  {
    id: 'aktivitaeten',
    label: 'Verlauf',
    icon: History,
    order: 25,
    render: (ctx) => <ActivityFeedTab patientId={ctx.patientId} orgId={ctx.orgId} />,
  },
  {
    id: 'nachrichten',
    label: 'Nachrichten',
    icon: MessageSquare,
    order: 78,
    featureFlag: 'messaging',
    pinned: true,
    render: (ctx) => <PatientMessagesTab patientId={ctx.patientId} orgId={ctx.orgId} />,
  },
  {
    id: 'documents',
    label: 'Dateien',
    icon: FolderOpen,
    order: 80,
    featureFlag: 'patient_portal',
    render: (ctx) => <DocumentsTab patientId={ctx.patientId} orgId={ctx.orgId} />,
  },
  {
    id: 'portal',
    label: 'Portal',
    icon: Smartphone,
    order: 85,
    featureFlag: 'patient_portal',
    // Container fasst Portal-Zugriff (Funktionsbaum + Check-ins) und
    // Portal-Aktivität (Therapeut-Einsicht) zu einem Tab zusammen. Spart
    // einen Top-Level-Slot in der Tab-Bar (UX-Audit Phase 1.2).
    render: (ctx) => <PortalTab ctx={ctx} />,
  },
  {
    id: 'klinik',
    label: 'Klinik',
    icon: Hospital,
    order: 90,
    // Kern-Tab im klinischen Kontext: `isEnabled` unten lässt den Tab
    // ohnehin nur für Klinik-Orgs bzw. stationäre Patienten durch —
    // wenn er sichtbar ist, gehört er in die Bar, nicht hinter "Mehr".
    pinned: true,
    featureFlag: 'station_overview',
    // The container needs both the klinik_modul feature and a clinical
    // context (org = klinik OR admitted patient). We fold both checks
    // into `isEnabled` so the container is hidden for ambulatory praxes
    // even if they have the klinik feature ticked.
    isEnabled: ({ hasFeature, isInpatient, settingType }) => {
      if (!hasFeature('klinik_modul')) return false;
      return settingType === 'klinik' || isInpatient;
    },
    render: (ctx) => <KlinikTab ctx={ctx} />,
  },

  // ── Hidden tabs ────────────────────────────────────────────────────────
  // Kept reachable via deep link + command palette, but removed from the
  // tab bar per the Lean & Clean plan. Outcome-Tracking is genuinely hard
  // to validate in an ernährungstherapy context, so we stop pushing it
  // into everybody's daily workflow.
  {
    id: 'outcome',
    label: 'Outcome-Tracking',
    icon: TrendingUp,
    feature: 'outcome_tracking',
    visibility: 'hidden',
    render: (ctx) => (
      <OutcomeTrackingTab patientId={ctx.patientId} orgId={ctx.orgId} />
    ),
  },
  {
    id: 'ki_companion',
    label: 'KI-Companion',
    icon: Sparkles,
    // No `featureFlag`: gated purely by the `ai_native` basis feature.
    // NutriKnows-RAG (`nutriknows_rag`) is an unrelated knowledge module —
    // its static flag must not kill the sold KI-Companion ("sold ⇒
    // reachable").
    feature: 'ai_native',
    order: 95,
    render: (ctx) => (
      <PatientKiCompanionPanel patientId={ctx.patientId} orgId={ctx.orgId} />
    ),
  },
];

/**
 * Backwards-compatible redirect map: a URL that still points at an old
 * leaf-tab id (`?tab=sessions`, `?tab=anthro`, …) is rewritten to the
 * new container id on load. Saves every existing bookmark without the
 * need for a database migration.
 *
 * This is intentionally *outbound*-only: old id → new id. Sub-tab
 * positioning within a container is not persisted in the URL in PR 1.3,
 * so a deep link to `?tab=gncp` lands on the Beratung container at its
 * default sub-tab. That is an accepted limitation; PR 3.x (Timeline)
 * will reshape Beratung into a single view and make the point moot.
 */
export const PATIENT_TAB_REDIRECTS: Readonly<Record<string, string>> = {
  // merged into 'beratung'
  termine: 'beratung',
  sessions: 'beratung',
  gncp: 'beratung',
  // merged into 'messwerte'
  anthro: 'messwerte',
  lab: 'messwerte',
  // merged into 'plan' container
  diary: 'plan',
  // merged into 'analyse'
  bls: 'analyse',
  recipe: 'analyse',
  // merged into 'anamnese-fragebogen'
  anamnese: 'anamnese-fragebogen',
  forms: 'anamnese-fragebogen',
  // merged into 'verordnungen-aufgaben'
  hmv: 'verordnungen-aufgaben',
  tasks: 'verordnungen-aufgaben',
  // merged into 'klinik'
  clinic: 'klinik',
  mna: 'klinik',
  iddsi: 'klinik',
  nursing: 'klinik',
  glim: 'klinik',
  // merged into 'portal' container (UX-Audit Phase 1.2)
  'portal-access': 'portal',
  'portal-activity': 'portal',
};

/**
 * Compute which tabs the current user should see. Pure function so it
 * can be unit-tested without a React tree.
 */
export function resolvePatientTabs(args: {
  hasFeature: (key: Feature) => boolean;
  isInpatient: boolean;
  /**
   * Org's setting type. `'klinik'` treats every patient as an inpatient
   * for tab-gating purposes. `'praxis'` keeps the `inpatientOnly`
   * restriction individual-patient-scoped.
   */
  settingType?: SettingType;
  /**
   * Superadmins bypass the `scope` filter to retain support access to
   * every tab regardless of org setting-type. Does not affect feature
   * flags or `inpatientOnly` — those remain enforced.
   */
  isSuperadmin?: boolean;
  /**
   * Org-weite Sichtbarkeits-Overrides (`organizations.patient_tab_overrides`,
   * gleiche Form wie `nav_overrides` — siehe `src/features/visibility.ts`).
   * Ein Override gewinnt über den statischen `tab.visibility`-Default.
   * Nur nicht-gepinnte Tabs sind über die UI editierbar (siehe
   * `WorkspacePreferences.tsx`), aber diese Funktion wendet jeden
   * Override unabhängig davon an — pinned-Status entscheidet nur, ob die
   * Einstellungsseite den Tab zum Anpassen anbietet.
   */
  overrides?: NavOverrides | null;
}): PatientTabModule[] {
  const settingType: SettingType = args.settingType ?? 'praxis';
  const isKlinikOrg = settingType === 'klinik';
  const isSuperadmin = args.isSuperadmin ?? false;
  const enablementCtx: PatientTabEnablementContext = {
    hasFeature: args.hasFeature,
    isInpatient: args.isInpatient,
    settingType,
  };

  const visible = PATIENT_TABS.filter((tab) => {
    const effectiveVisibility = args.overrides?.[tab.id] ?? tab.visibility ?? 'primary';
    if (effectiveVisibility === 'hidden') return false;
    if (tab.isEnabled) {
      if (!tab.isEnabled(enablementCtx)) return false;
    } else if (tab.feature && !args.hasFeature(tab.feature)) {
      return false;
    }
    if (tab.inpatientOnly && !isKlinikOrg && !args.isInpatient) return false;
    if (
      !isSuperadmin &&
      tab.scope &&
      tab.scope !== 'both' &&
      tab.scope !== settingType
    ) {
      return false;
    }
    return true;
  })
    .slice()
    .sort((a, b) => (a.order ?? 1000) - (b.order ?? 1000));

  // Klinik: die "Verordnungen"-Schiene heißt hier "Konsil" — die Indikation
  // kommt über das ärztliche Konsil, nicht über eine Heilmittelverordnung.
  if (isKlinikOrg) {
    return visible.map((t) =>
      t.id === 'verordnungen-aufgaben' ? { ...t, label: 'Konsil' } : t,
    );
  }
  return visible;
}

/** Helper: get the tab module by id; returns `undefined` for unknown ids. */
export function getPatientTab(id: string): PatientTabModule | undefined {
  return PATIENT_TABS.find((t) => t.id === id);
}

/**
 * Resolve a tab id that may come from an old bookmark to the current
 * (post-PR 1.3) id. Returns the input unchanged if no redirect applies.
 */
export function redirectPatientTabId(id: string): string {
  return PATIENT_TAB_REDIRECTS[id] ?? id;
}

/** Ergebnis von {@link partitionPatientTabs}. */
export interface PatientTabPartition {
  /** Tabs, die direkt in der Tab-Bar gerendert werden (Kern + aktiver Tab). */
  bar: PatientTabModule[];
  /** Alle übrigen Tabs — landen hinter dem "Mehr"-Launcher (Tile-Grid). */
  overflow: PatientTabModule[];
}

/**
 * Kuratierte Akte: teilt die bereits gegateten, sortierten Tabs (Output
 * von `resolvePatientTabs` + featureFlag-Filter) in Bar und Overflow.
 *
 *   - Gepinnte Kern-Tabs (`pinned: true`) sind immer in der Bar.
 *   - Der aktive Tab ist IMMER in der Bar, auch wenn er nicht gepinnt
 *     ist — der User sieht jederzeit, wo er sich befindet (Prinzip 1:
 *     Status sichtbar). Deep links (`?tab=X`) funktionieren dadurch
 *     unverändert.
 *   - Alles andere wandert in den Overflow ("Mehr"-Dialog).
 *
 * Pure function ohne React, damit sie sich gemeinsam mit
 * `resolvePatientTabs` unit-testen lässt. Die Eingabe-Reihenfolge
 * bleibt in beiden Buckets erhalten.
 */
export function partitionPatientTabs(
  tabs: readonly PatientTabModule[],
  activeTabId: string,
): PatientTabPartition {
  const bar: PatientTabModule[] = [];
  const overflow: PatientTabModule[] = [];
  for (const tab of tabs) {
    if (tab.pinned || tab.id === activeTabId) {
      bar.push(tab);
    } else {
      overflow.push(tab);
    }
  }
  return { bar, overflow };
}
